import { useEffect, useState, useCallback } from 'react';
import { Loader2, CheckCircle2, XCircle, RefreshCw, RotateCcw, X } from 'lucide-react';
import { fieldPartnersApi, type FieldPartnerKyc } from '@/api/field-partners';
import { formatDate } from '@/utils/formatters';
import { FieldProgramGate } from '@/components/field-partners/FieldProgramGate';
import type { FieldProgramConfig } from '@/hooks/useFieldProgramConfig';

function KycReviewInner({ config }: { config: FieldProgramConfig | null }) {
  const partnerLabel = config?.partnerTypeLabel || 'Field Partner';
  const kycVendor = config?.kycVendor || 'kyc';

  const [rows, setRows] = useState<FieldPartnerKyc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<FieldPartnerKyc | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fieldPartnersApi.listKycQueue();
      setRows(data);
    } catch (err: any) {
      if (err.response?.status === 404) setRows([]);
      else setError(err.response?.data?.error || 'Failed to load KYC queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const act = async (fn: () => Promise<any>) => {
    try {
      await fn();
      setSelected(null);
      setShowReject(false);
      setRejectReason('');
      await fetch();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Action failed');
    }
  };

  if (config && !config.kycRequired) {
    return (
      <div className="mx-auto max-w-xl rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-10 text-center">
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">KYC is disabled</h2>
        <p className="mt-1 text-sm text-[var(--text-muted)]">
          KYC review is turned off for this tenant. Enable it in Program Config to start collecting and reviewing KYC submissions.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">KYC Review Queue</h1>
          <p className="text-sm text-[var(--text-muted)]">
            Pending and manual-review KYC cases for {partnerLabel.toLowerCase()}s ({kycVendor})
          </p>
        </div>
        <button
          onClick={fetch}
          className="flex items-center gap-2 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)]">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          </div>
        ) : error ? (
          <div className="py-20 text-center text-sm text-red-600">{error}</div>
        ) : rows.length === 0 ? (
          <div className="py-20 text-center text-sm text-[var(--text-muted)]">
            No pending KYC cases
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-primary)] bg-[var(--bg-inset)] text-left text-xs font-medium uppercase tracking-wider text-[var(--text-muted)]">
                <th className="px-4 py-3">{partnerLabel}</th>
                <th className="px-4 py-3">Aadhaar (masked)</th>
                <th className="px-4 py-3">PAN</th>
                <th className="px-4 py-3">Vendor Status</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-primary)]">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-[var(--bg-surface-hover)]">
                  <td className="px-4 py-3 font-medium text-[var(--text-primary)]">
                    {r.partnerName}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--text-secondary)]">
                    {r.aadhaarMasked || '-'}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[var(--text-secondary)]">
                    {r.pan || '-'}
                  </td>
                  <td className="px-4 py-3 text-[var(--text-secondary)]">
                    {r.vendorStatus || 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-[var(--text-secondary)]">
                    {formatDate(r.submittedAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setSelected(r)}
                      className="text-sm text-[var(--ink-0)] hover:underline"
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--border-primary)] px-4 py-3">
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">
                KYC Detail — {selected.partnerName}
              </h2>
              <button
                onClick={() => setSelected(null)}
                className="rounded p-1 text-[var(--text-muted)] hover:bg-[var(--bg-surface-hover)]"
              >
                <X size={16} />
              </button>
            </div>
            <div className="max-h-[70vh] space-y-4 overflow-y-auto p-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs uppercase text-[var(--text-muted)]">Aadhaar (masked)</p>
                  <p className="font-mono text-[var(--text-primary)]">{selected.aadhaarMasked || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-[var(--text-muted)]">PAN</p>
                  <p className="font-mono text-[var(--text-primary)]">{selected.pan || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-[var(--text-muted)]">Bank Account</p>
                  <p className="font-mono text-[var(--text-primary)]">{selected.bankAccount || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-[var(--text-muted)]">IFSC</p>
                  <p className="font-mono text-[var(--text-primary)]">{selected.bankIfsc || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-[var(--text-muted)]">UPI VPA</p>
                  <p className="font-mono text-[var(--text-primary)]">{selected.upiVpa || '-'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-[var(--text-muted)]">Vendor Status</p>
                  <p className="text-[var(--text-primary)]">{selected.vendorStatus || '-'}</p>
                </div>
              </div>

              {selected.photoUrls.length > 0 && (
                <div>
                  <p className="mb-2 text-xs uppercase text-[var(--text-muted)]">Photos</p>
                  <div className="grid grid-cols-3 gap-2">
                    {selected.photoUrls.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt={`kyc-${i}`}
                        className="h-32 w-full rounded-lg border border-[var(--border-primary)] object-cover"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-[var(--border-primary)] px-4 py-3">
              <button
                onClick={() => act(() => fieldPartnersApi.requestKycResubmit(selected.id))}
                className="flex items-center gap-1 rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-100"
              >
                <RotateCcw size={14} /> Request Re-submission
              </button>
              <button
                onClick={() => setShowReject(true)}
                className="flex items-center gap-1 rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-sm text-red-700 hover:bg-red-100"
              >
                <XCircle size={14} /> Reject
              </button>
              <button
                onClick={() => act(() => fieldPartnersApi.approveKyc(selected.id))}
                className="flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
              >
                <CheckCircle2 size={14} /> Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {showReject && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4 shadow-xl">
            <h3 className="mb-2 text-sm font-semibold text-[var(--text-primary)]">
              Rejection reason
            </h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-inset)] p-2 text-sm text-[var(--text-primary)]"
              placeholder="Describe the reason..."
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                onClick={() => setShowReject(false)}
                className="rounded-lg border border-[var(--border-primary)] px-3 py-1.5 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  rejectReason.trim()
                    ? act(() => fieldPartnersApi.rejectKyc(selected.id, rejectReason.trim()))
                    : alert('Reason required')
                }
                className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function KycReviewPage() {
  return <FieldProgramGate>{(config) => <KycReviewInner config={config} />}</FieldProgramGate>;
}

export default KycReviewPage;
